import React from 'react';
import {
  Alert,
  Button,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  createSupportAction,
  getSupportActions,
  type SupportActionLog,
} from '../services/apiService.ts';

const EMPTY_FORM = {
  tenantId: '',
  locationId: '',
  targetEntity: '',
  targetId: '',
  actionCode: '',
  reason: '',
  result: '',
  payloadJson: '',
};

const formatDateTime = (value?: string | null) => {
  if (!value) {
    return '-';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return new Intl.DateTimeFormat('ro-RO', {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(date);
};

const formatCodeLabel = (value?: string | null, fallback = '-') => {
  if (!value) {
    return fallback;
  }
  return value
    .toLowerCase()
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
};

const SupportActionsPage: React.FC = () => {
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [rows, setRows] = React.useState<SupportActionLog[]>([]);
  const [query, setQuery] = React.useState('');
  const [form, setForm] = React.useState(EMPTY_FORM);

  const load = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const page = await getSupportActions({ query, page: 0, size: 50 });
      setRows(Array.isArray(page?.content) ? page.content : []);
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Nu am putut incarca jurnalul de suport.');
    } finally {
      setLoading(false);
    }
  }, [query]);

  React.useEffect(() => {
    load();
  }, [load]);

  const submit = async () => {
    setSaving(true);
    setError(null);
    try {
      await createSupportAction({
        tenantId: form.tenantId || null,
        locationId: form.locationId || null,
        targetEntity: form.targetEntity,
        targetId: form.targetId,
        actionCode: form.actionCode,
        reason: form.reason,
        result: form.result || undefined,
        payloadJson: form.payloadJson || undefined,
      });
      setForm(EMPTY_FORM);
      await load();
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Nu am putut salva actiunea de suport.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Stack spacing={3}>
      <Stack spacing={1}>
        <Typography variant="overline" color="text.secondary">
          Internal Audit
        </Typography>
        <Typography variant="h4">Support Actions</Typography>
        <Typography color="text.secondary">
          Override-urile si interventiile interne trebuie sa lase motiv, tinta si rezultat in audit.
        </Typography>
      </Stack>

      {error ? <Alert severity="error">{error}</Alert> : null}

      <Paper sx={{ p: 3 }}>
        <Stack spacing={2}>
          <Typography variant="h6">Log New Action</Typography>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <TextField label="Tenant ID" value={form.tenantId} onChange={(event) => setForm((prev) => ({ ...prev, tenantId: event.target.value }))} size="small" />
            <TextField label="Location ID" value={form.locationId} onChange={(event) => setForm((prev) => ({ ...prev, locationId: event.target.value }))} size="small" />
            <TextField label="Target entity" value={form.targetEntity} onChange={(event) => setForm((prev) => ({ ...prev, targetEntity: event.target.value }))} size="small" />
            <TextField label="Target ID" value={form.targetId} onChange={(event) => setForm((prev) => ({ ...prev, targetId: event.target.value }))} size="small" />
          </Stack>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <TextField label="Action code" value={form.actionCode} onChange={(event) => setForm((prev) => ({ ...prev, actionCode: event.target.value }))} size="small" fullWidth />
            <TextField label="Result" value={form.result} onChange={(event) => setForm((prev) => ({ ...prev, result: event.target.value }))} size="small" fullWidth />
          </Stack>
          <TextField label="Reason" value={form.reason} onChange={(event) => setForm((prev) => ({ ...prev, reason: event.target.value }))} size="small" fullWidth multiline minRows={2} />
          <TextField label="Payload JSON (optional)" value={form.payloadJson} onChange={(event) => setForm((prev) => ({ ...prev, payloadJson: event.target.value }))} size="small" fullWidth multiline minRows={3} />
          <Button variant="contained" onClick={submit} disabled={saving}>
            {saving ? 'Saving...' : 'Save support action'}
          </Button>
        </Stack>
      </Paper>

      <Paper sx={{ p: 3 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
          <TextField
            label="Search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            size="small"
            fullWidth
          />
          <Button variant="outlined" onClick={load} disabled={loading}>
            {loading ? 'Loading...' : 'Refresh'}
          </Button>
        </Stack>
      </Paper>

      <Paper sx={{ overflowX: 'auto' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>When</TableCell>
              <TableCell>Actor</TableCell>
              <TableCell>Target</TableCell>
              <TableCell>Action</TableCell>
              <TableCell>Reason</TableCell>
              <TableCell>Result</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.id}>
                <TableCell>{formatDateTime(row.createdAt)}</TableCell>
                <TableCell>{row.actorUserId ? `User #${row.actorUserId}` : 'System'}</TableCell>
                <TableCell>{`${formatCodeLabel(row.targetEntity)} #${row.targetId}`}</TableCell>
                <TableCell>{formatCodeLabel(row.actionCode)}</TableCell>
                <TableCell>{row.reason}</TableCell>
                <TableCell>{formatCodeLabel(row.result)}</TableCell>
              </TableRow>
            ))}
            {!rows.length && !loading ? (
              <TableRow>
                <TableCell colSpan={6}>
                  <Typography color="text.secondary">
                    Nu exista actiuni de suport pentru filtrul curent.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : null}
          </TableBody>
        </Table>
      </Paper>
    </Stack>
  );
};

export default SupportActionsPage;
